import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Key, Users, Save, UserPlus, Trash2, Camera, MapPin, Wheat, Leaf, Image as ImageIcon, Plus, Upload, X, AlertTriangle, ShieldCheck } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ApiService from '../services/apiService';
import Card from '../components/ui/Card';
import { sanitizeMobile, validateMobile, computeAge } from '../utils/validationRules';

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationPicker({ lat, lng, setLocation }) {
  useMapEvents({
    click(e) {
      setLocation(e.latlng.lat, e.latlng.lng);
    },
  });
  
  const map = useMap();
  useEffect(() => {
    if (lat && lng) {
      map.setView([lat, lng], map.getZoom());
    }
  }, [lat, lng, map]);

  return lat && lng ? <Marker position={[lat, lng]} /> : null;
}
const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.includes('127.0.0.1:8009') || url.includes('localhost:8009')) {
        let cleanPath = url.replace('http://127.0.0.1:8009', '').replace('http://localhost:8009', '');
        if (!cleanPath.startsWith('/')) cleanPath = '/' + cleanPath;
        return `https://geonutria.ai${cleanPath}`;
    }
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    const path = url.startsWith('/') ? url : `/${url}`;
    return `https://geonutria.ai${path}`;
  };
  // -------------------------

export default function ProfileView({ userId, t, isRTL }) {
  const [profile, setProfile] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [activeSubTab, setActiveSubTab] = useState('profile'); // 'profile' | 'assets'
  
  // Forms
  const [formData, setFormData] = useState({ name: '', mobile: '', date_of_birth: '', sex: '' });
  const [passData, setPassData] = useState({ old_password: '', new_password: '' });
  const [teamData, setTeamData] = useState({ member_email: '', shared_credits: '' });
  const [passError, setPassError] = useState('');
  const [mobileError, setMobileError] = useState('');
  
  // Asset Management State
  const [farms, setFarms] = useState([]);
  const [crops, setCrops] = useState([]);
  const [trees, setTrees] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [selectedEntityForMedia, setSelectedEntityForMedia] = useState(null); // { type: 'Farm'|'Crop'|'Tree', id: number }
  const [mediaGallery, setMediaGallery] = useState([]);
  
  // Create Forms
  const [newFarm, setNewFarm] = useState({ farm_name: '', address: '', total_area: '', latitude: 30.0444, longitude: 31.2357 });
  const [newCrop, setNewCrop] = useState({ crop_name: '', crop_category: 'Cereal', planted_area: '', age: '', water_consumption: '', health_status: 'Healthy', yield_capacity: '' });
  const [newTree, setNewTree] = useState({ tree_name: '', tree_code: '', area: '', latitude: 30.0444, longitude: 31.2357, age: '', water_consumption: '', health_status: 'Healthy', yield_capacity: '' });

  const fileInputRef = useRef(null);
  const multipleFileInputRef = useRef(null);

  useEffect(() => {
    fetchProfileData();
    fetchFarms();
  }, [userId]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const [profileRes, teamRes] = await Promise.all([
        ApiService.get(`/profile/?user_id=${userId}`),
        ApiService.get(`/profile/team?user_id=${userId}`)
      ]);
      setProfile(profileRes);
      setFormData({
        name: profileRes.name || '',
        mobile: profileRes.mobile || '',
        date_of_birth: profileRes.date_of_birth || '',
        sex: profileRes.sex || ''
      });
      setTeam(teamRes);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchFarms = async () => {
    try {
      const res = await ApiService.get(`/assets/farms?user_id=${userId}`);
      setFarms(res);
    } catch (e) { console.error(e); }
  };

  const fetchCrops = async (farmId) => {
    try {
      const res = await ApiService.get(`/assets/crops?farm_id=${farmId}`);
      setCrops(res);
    } catch (e) { console.error(e); }
  };

  const fetchTrees = async (cropId) => {
    try {
      const res = await ApiService.get(`/assets/trees?crop_id=${cropId}`);
      setTrees(res);
    } catch (e) { console.error(e); }
  };

  const fetchMedia = async (type, id) => {
    try {
      const res = await ApiService.get(`/assets/media?entity_type=${type}&entity_id=${id}`);
      setMediaGallery(res);
    } catch (e) { console.error(e); }
  };

  // Profile Handlers
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await ApiService.put(`/profile/update?user_id=${userId}`, formData);
      alert(t.profile?.saveChanges + " ✅");
      fetchProfileData();
    } catch (e) { alert("Error updating profile"); }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPassError('');
    // Client-side validation
    if (passData.new_password.length < 8) {
      setPassError(isRTL ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل.' : 'Password must be at least 8 characters.');
      return;
    }
    try {
      await ApiService.post(`/profile/password?user_id=${userId}`, passData);
      alert(isRTL ? 'تم تحديث كلمة المرور ✅' : 'Password updated ✅');
      setPassData({ old_password: '', new_password: '' });
      fetchProfileData();
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('500') || msg.toLowerCase().includes('internal server error')) {
        setPassError(isRTL ? 'كلمة المرور لا تستوفي متطلبات الأمان. يجب أن تحتوي على 8 أحرف على الأقل، حرف كبير واحد، ورقم واحد.' : 'Password does not meet security requirements. Must contain at least 8 characters, one uppercase letter, and one number.');
      } else if (msg.includes('Incorrect old password') || msg.includes('400')) {
        setPassError(isRTL ? 'كلمة المرور القديمة غير صحيحة.' : 'The old password you entered is incorrect.');
      } else {
        setPassError(isRTL ? 'فشل تحديث كلمة المرور. حاول مرة أخرى.' : 'Failed to update password. Please try again.');
      }
    }
  };

  const handleAddTeamMember = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        member_email: teamData.member_email,
        shared_credits: teamData.shared_credits ? parseInt(teamData.shared_credits) : null
      };
      await ApiService.post(`/profile/team/add?user_id=${userId}`, payload);
      setTeamData({ member_email: '', shared_credits: '' });
      fetchProfileData();
    } catch (e) { alert(e.message || "Error adding member"); }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await ApiService.delete(`/profile/team/remove/${memberId}?user_id=${userId}`);
      fetchProfileData();
    } catch (e) { alert("Error removing member"); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const data = new FormData();
    data.append('user_id', userId);
    data.append('file', file);
    try {
      await ApiService.post('/profile/picture', data, true);
      fetchProfileData();
    } catch (err) { alert("Upload failed"); }
  };

  // Asset Handlers
  const handleCreateFarm = async (e) => {
    e.preventDefault();
    try {
      await ApiService.post(`/assets/farms?user_id=${userId}`, {
        farm_name: newFarm.farm_name,
        address: newFarm.address,
        total_area: parseFloat(newFarm.total_area || 0),
        latitude: newFarm.latitude,
        longitude: newFarm.longitude
      });
      fetchFarms();
      setNewFarm({ ...newFarm, farm_name: '', address: '', total_area: '' });
    } catch (e) { alert("Error creating farm"); }
  };

  const handleCreateCrop = async (e) => {
    e.preventDefault();
    if (!selectedFarm) return;
    try {
      await ApiService.post(`/assets/crops`, {
        farm_id: selectedFarm.id,
        crop_name: newCrop.crop_name,
        crop_category: newCrop.crop_category,
        planted_area: parseFloat(newCrop.planted_area || 0),
        age: parseInt(newCrop.age || 0),
        water_consumption: parseFloat(newCrop.water_consumption || 0),
        health_status: newCrop.health_status,
        yield_capacity: parseFloat(newCrop.yield_capacity || 0)
      });
      fetchCrops(selectedFarm.id);
      setNewCrop({ ...newCrop, crop_name: '', planted_area: '', age: '', water_consumption: '', yield_capacity: '' });
    } catch (e) { alert("Error creating crop"); }
  };

  const handleCreateTree = async (e) => {
    e.preventDefault();
    if (!selectedCrop) return;
    try {
      await ApiService.post(`/assets/trees`, {
        crop_id: selectedCrop.id,
        tree_name: newTree.tree_name,
        tree_code: newTree.tree_code,
        area: parseFloat(newTree.area || 0),
        latitude: newTree.latitude,
        longitude: newTree.longitude,
        age: parseInt(newTree.age || 0),
        water_consumption: parseFloat(newTree.water_consumption || 0),
        health_status: newTree.health_status,
        yield_capacity: parseFloat(newTree.yield_capacity || 0)
      });
      fetchTrees(selectedCrop.id);
      setNewTree({ ...newTree, tree_name: '', tree_code: '', area: '', age: '', water_consumption: '', yield_capacity: '' });
    } catch (e) { alert("Error creating tree"); }
  };

  const handleDeleteEntity = async (type, id) => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    try {
      await ApiService.delete(`/assets/${type.toLowerCase()}s/${id}`);
      if (type === 'Farm') {
        setSelectedFarm(null); setSelectedCrop(null);
        fetchFarms();
      } else if (type === 'Crop') {
        setSelectedCrop(null);
        fetchCrops(selectedFarm.id);
      } else {
        fetchTrees(selectedCrop.id);
      }
    } catch (e) { alert(`Error deleting ${type}`); }
  };

  const handleMultiMediaUpload = async (e) => {
    const files = e.target.files;
    if (!files.length || !selectedEntityForMedia) return;
    
    const data = new FormData();
    data.append('user_id', userId);
    data.append('entity_type', selectedEntityForMedia.type);
    data.append('entity_id', selectedEntityForMedia.id);
    for (let i = 0; i < files.length; i++) {
      data.append('files', files[i]);
    }
    
    try {
      await ApiService.post('/assets/upload-media', data, true);
      fetchMedia(selectedEntityForMedia.type, selectedEntityForMedia.id);
    } catch (err) { alert("Media Upload failed"); }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Top Navigation */}
      <div className="flex justify-center border-b border-[#1B263B]/10 dark:border-white/10 mb-6">
        <div className="flex gap-8">
          <button onClick={() => setActiveSubTab('profile')} className={`pb-3 font-semibold transition-colors ${activeSubTab === 'profile' ? 'text-[#C47A2C] border-b-2 border-[#C47A2C]' : 'text-[#1B263B]/50 dark:text-white/50 hover:text-[#1B263B] dark:hover:text-white'}`}>
            Profile Settings
          </button>
          <button onClick={() => setActiveSubTab('assets')} className={`pb-3 font-semibold transition-colors ${activeSubTab === 'assets' ? 'text-[#C47A2C] border-b-2 border-[#C47A2C]' : 'text-[#1B263B]/50 dark:text-white/50 hover:text-[#1B263B] dark:hover:text-white'}`}>
            Farms & Assets
          </button>
        </div>
      </div>

      {activeSubTab === 'profile' && (
        <>
          {/* HEADER */}
          <div className="flex items-center gap-4 mb-8">
            <div className="relative group">
              {profile?.picture ? (
                <img 
                  src={getFullImageUrl(profile.picture)} 
                  alt="Profile" 
                  className="w-20 h-20 rounded-full object-cover border-4 border-white dark:border-[#1B263B] shadow-lg"
                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://ui-avatars.com/api/?name=' + (profile?.name || 'User') + '&background=C47A2C&color=fff'; }}
                />
              ) : (
                <div className="w-20 h-20 rounded-full flex items-center justify-center border-4 border-white dark:border-[#1B263B] shadow-lg bg-[#C47A2C] text-white">
                  <User size={36} />
                </div>
              )}
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all"
              >
                <Camera size={20} />
              </button>
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handlePhotoUpload} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#1B263B] dark:text-white">{profile?.name || 'User'}</h2>
              <div className="flex items-center gap-2 text-sm text-[#1B263B]/60 dark:text-white/60 mt-1">
                <span className="bg-[#C47A2C]/10 text-[#C47A2C] px-2 py-0.5 rounded-md font-medium">{profile?.subscription_plan || 'Free'}</span>
                <span>•</span>
                <span>{profile?.email}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* PERSONAL INFO */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4 border-b border-[#1B263B]/10 dark:border-white/10 pb-4">
                <User className="text-[#C47A2C]" />
                <h3 className="text-lg font-bold">{t.profile?.personalInfo || 'Personal Information'}</h3>
              </div>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-1">{t.profile?.name || 'Name'}</label>
                  <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-[#1B263B]/5 dark:bg-white/5 border border-transparent focus:border-[#C47A2C]/50 rounded-xl px-4 py-2 text-sm outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">{t.profile?.mobile || 'Mobile'}</label>
                  <input 
                    type="tel" 
                    inputMode="numeric"
                    dir="ltr" 
                    style={{ direction: 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                    value={formData.mobile} 
                    onChange={e => {
                      const sanitized = sanitizeMobile(e.target.value);
                      setFormData({...formData, mobile: sanitized});
                      const result = validateMobile(sanitized, isRTL ? 'ar' : 'en');
                      setMobileError(result.status === 'error' ? result.message : '');
                    }} 
                    className={`w-full bg-[#1B263B]/5 dark:bg-white/5 border ${mobileError ? 'border-rose-400' : 'border-transparent'} focus:border-[#C47A2C]/50 rounded-xl px-4 py-2 text-sm outline-none transition-colors`}
                    placeholder="+20 1XX XXX XXXX"
                  />
                  {mobileError && <p className="text-xs text-rose-500 mt-1 font-medium">{mobileError}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1">{t.profile?.dateOfBirth || 'Date of Birth'}</label>
                    <input 
                      type="date" 
                      max={new Date().toISOString().split('T')[0]}
                      value={formData.date_of_birth} 
                      onChange={e => setFormData({...formData, date_of_birth: e.target.value})} 
                      className="w-full bg-[#1B263B]/5 dark:bg-white/5 border border-transparent focus:border-[#C47A2C]/50 rounded-xl px-4 py-2 text-sm outline-none" 
                    />
                    {formData.date_of_birth && (
                      <p className="text-xs text-[#6B8F71] mt-1 font-medium">
                        {isRTL ? `العمر: ${computeAge(formData.date_of_birth)} سنة` : `Age: ${computeAge(formData.date_of_birth)} years`}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">{t.profile?.gender || 'Gender'}</label>
                    <select value={formData.sex} onChange={e => setFormData({...formData, sex: e.target.value})} className="w-full bg-[#1B263B]/5 dark:bg-white/5 border border-transparent focus:border-[#C47A2C]/50 rounded-xl px-4 py-2 text-sm outline-none">
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <button type="submit" className="w-full bg-[#C47A2C] text-white rounded-xl py-2 font-medium hover:bg-[#A36322] transition flex items-center justify-center gap-2">
                  <Save size={16} /> {t.profile?.saveChanges || 'Save Changes'}
                </button>
              </form>
            </Card>

            <div className="space-y-6">
              {/* PASSWORD */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4 border-b border-[#1B263B]/10 dark:border-white/10 pb-4">
                  <Key className="text-[#C47A2C]" />
                  <h3 className="text-lg font-bold">{profile?.has_password ? (t.profile?.updatePassword || 'Update Password') : 'Create Password'}</h3>
                </div>
                <form onSubmit={handleUpdatePassword} className="space-y-4">
                  {profile?.has_password && (
                    <div>
                      <label className="block text-xs font-medium mb-1">{t.profile?.oldPassword || 'Old Password'}</label>
                      <input type="password" value={passData.old_password} onChange={e => setPassData({...passData, old_password: e.target.value})} className="w-full bg-[#1B263B]/5 dark:bg-white/5 border border-transparent focus:border-[#C47A2C]/50 rounded-xl px-4 py-2 text-sm outline-none" required />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium mb-1">{t.profile?.newPassword || 'New Password'}</label>
                    <input type="password" value={passData.new_password} onChange={e => setPassData({...passData, new_password: e.target.value})} className="w-full bg-[#1B263B]/5 dark:bg-white/5 border border-transparent focus:border-[#C47A2C]/50 rounded-xl px-4 py-2 text-sm outline-none" required />
                  </div>
                  <button type="submit" className="w-full bg-[#1B263B] dark:bg-white/10 text-white rounded-xl py-2 font-medium hover:bg-black transition">
                    {profile?.has_password ? (t.profile?.updatePassword || 'Update Password') : 'Create Password'}
                  </button>
                  {passError && (
                    <div className="flex items-start gap-2 p-3 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 rounded-xl text-rose-600 dark:text-rose-400 text-xs animate-fade-in">
                      <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold mb-1">{passError}</p>
                        <div className="flex items-center gap-1 text-[10px] opacity-70 mt-1">
                          <ShieldCheck size={12} />
                          <span>{isRTL ? 'متطلبات: 8+ أحرف، حرف كبير، رقم' : 'Requirements: 8+ chars, uppercase, number'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </Card>

              {/* TEAM MANAGEMENT */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4 border-b border-[#1B263B]/10 dark:border-white/10 pb-4">
                  <Users className="text-[#C47A2C]" />
                  <h3 className="text-lg font-bold">{t.profile?.teamManagement || 'Team Management'}</h3>
                </div>
                <div className="space-y-3 mb-6 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                  {team.length === 0 ? (
                    <div className="text-center text-sm text-gray-500 py-2">No team members yet.</div>
                  ) : team.map(m => (
                    <div key={m.member_id} className="flex items-center justify-between bg-[#1B263B]/5 dark:bg-white/5 p-3 rounded-xl">
                      <div className="flex items-center gap-3">
                       <img src={m.picture ? getFullImageUrl(m.picture) : `https://ui-avatars.com/api/?name=${m.name}`} className="w-8 h-8 rounded-full" alt="Team member" />
                        <div>
                          <div className="text-sm font-bold">{m.name}</div>
                          <div className="text-xs opacity-60">{m.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-xs bg-[#C47A2C]/20 text-[#C47A2C] px-2 py-1 rounded-md">
                          {m.shared_credits} CR
                        </div>
                        <button onClick={() => handleRemoveMember(m.member_id)} className="text-red-500 hover:text-red-700 p-1">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <form onSubmit={handleAddTeamMember} className="space-y-3 p-4 bg-[#1B263B]/5 dark:bg-white/5 rounded-xl border border-dashed border-[#1B263B]/20 dark:border-white/20">
                  <div>
                    <label className="block text-xs font-medium mb-1">{t.profile?.memberEmail || 'Member Email'}</label>
                    <input type="email" value={teamData.member_email} onChange={e => setTeamData({...teamData, member_email: e.target.value})} className="w-full bg-white dark:bg-[#162030] border border-transparent focus:border-[#C47A2C]/50 rounded-lg px-3 py-1.5 text-sm outline-none" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">{t.profile?.sharedCredits || 'Shared Credits (optional)'}</label>
                    <input type="number" placeholder="Leave blank to share your entire available balance with this team." value={teamData.shared_credits} onChange={e => setTeamData({...teamData, shared_credits: e.target.value})} className="w-full bg-white dark:bg-[#162030] border border-transparent focus:border-[#C47A2C]/50 rounded-lg px-3 py-1.5 text-sm outline-none" />
                  </div>
                  <button type="submit" className="w-full bg-[#1B263B]/10 hover:bg-[#1B263B]/20 dark:bg-white/10 dark:hover:bg-white/20 text-[#1B263B] dark:text-white rounded-lg py-1.5 text-sm font-medium transition flex items-center justify-center gap-2 mt-2">
                    <UserPlus size={14} /> {t.profile?.addMember || 'Add Member'}
                  </button>
                </form>
              </Card>
            </div>
          </div>
        </>
      )}

      {activeSubTab === 'assets' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 h-[800px]">
          {/* FARMS LIST */}
          <Card className="flex flex-col h-full overflow-hidden">
            <div className="p-4 border-b border-[#1B263B]/10 dark:border-white/10 flex justify-between items-center bg-[#1B263B]/5 dark:bg-white/5">
              <h3 className="font-bold flex items-center gap-2"><MapPin size={18} className="text-[#C47A2C]"/> My Farms</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
              {farms.map(farm => (
                <div key={farm.id} onClick={() => { setSelectedFarm(farm); setSelectedCrop(null); fetchCrops(farm.id); }} className={`p-3 rounded-xl border cursor-pointer transition-all ${selectedFarm?.id === farm.id ? 'border-[#C47A2C] bg-[#C47A2C]/5' : 'border-[#1B263B]/10 dark:border-white/10 hover:border-[#C47A2C]/50'}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold">{farm.farm_name || 'Unnamed Farm'}</div>
                      <div className="text-xs opacity-60 mt-1">{farm.address}</div>
                      <div className="text-xs opacity-60 mt-1">{farm.total_area} Acres</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={(e) => { e.stopPropagation(); setSelectedEntityForMedia({ type: 'Farm', id: farm.id }); fetchMedia('Farm', farm.id); }} className="text-blue-500 p-1"><ImageIcon size={16}/></button>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteEntity('Farm', farm.id); }} className="text-red-500 p-1"><Trash2 size={16}/></button>
                    </div>
                  </div>
                </div>
              ))}

              <form onSubmit={handleCreateFarm} className="mt-6 p-4 bg-[#1B263B]/5 dark:bg-white/5 rounded-xl border border-dashed border-[#1B263B]/20">
                <h4 className="text-sm font-bold mb-3 flex items-center gap-2"><Plus size={14}/> Add New Farm</h4>
                <div className="space-y-2">
                  <input type="text" placeholder="Farm Name" value={newFarm.farm_name} onChange={e=>setNewFarm({...newFarm, farm_name:e.target.value})} className="w-full text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" required />
                  <input type="text" placeholder="Address" value={newFarm.address} onChange={e=>setNewFarm({...newFarm, address:e.target.value})} className="w-full text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" required />
                  <input type="number" placeholder="Total Area (Acres)" value={newFarm.total_area} onChange={e=>setNewFarm({...newFarm, total_area:e.target.value})} className="w-full text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" />
                  
                  <div className="flex gap-2">
                    <input type="number" placeholder="Lat" value={newFarm.latitude} onChange={e=>setNewFarm({...newFarm, latitude:parseFloat(e.target.value)})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" step="any" />
                    <input type="number" placeholder="Lng" value={newFarm.longitude} onChange={e=>setNewFarm({...newFarm, longitude:parseFloat(e.target.value)})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" step="any" />
                  </div>
                  
                  <div className="h-32 rounded overflow-hidden relative z-0">
                    <MapContainer center={[newFarm.latitude || 30.0444, newFarm.longitude || 31.2357]} zoom={5} style={{ height: '100%', width: '100%' }}>
                      <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                      <LocationPicker lat={newFarm.latitude} lng={newFarm.longitude} setLocation={(lat, lng) => setNewFarm({...newFarm, latitude: lat, longitude: lng})} />
                    </MapContainer>
                  </div>

                  <button type="submit" className="w-full bg-[#C47A2C] text-white rounded p-2 text-sm font-bold">Add Farm</button>
                </div>
              </form>
            </div>
          </Card>

          {/* CROPS LIST */}
          <Card className={`flex flex-col h-full overflow-hidden transition-opacity ${!selectedFarm ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="p-4 border-b border-[#1B263B]/10 dark:border-white/10 flex justify-between items-center bg-[#1B263B]/5 dark:bg-white/5">
              <h3 className="font-bold flex items-center gap-2"><Wheat size={18} className="text-[#C47A2C]"/> Crops in {selectedFarm?.farm_name || 'Farm'}</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
              {crops.map(crop => (
                <div key={crop.id} onClick={() => { setSelectedCrop(crop); fetchTrees(crop.id); }} className={`p-3 rounded-xl border cursor-pointer transition-all ${selectedCrop?.id === crop.id ? 'border-[#C47A2C] bg-[#C47A2C]/5' : 'border-[#1B263B]/10 dark:border-white/10 hover:border-[#C47A2C]/50'}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold">{crop.crop_name}</div>
                      <div className="text-xs opacity-60 mt-1">Area: {crop.planted_area} | Category: {crop.crop_category}</div>
                      <div className="text-xs opacity-60 mt-1">Yield Cap: {crop.yield_capacity} | Age: {crop.age} | Health: {crop.health_status}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={(e) => { e.stopPropagation(); setSelectedEntityForMedia({ type: 'Crop', id: crop.id }); fetchMedia('Crop', crop.id); }} className="text-blue-500 p-1"><ImageIcon size={16}/></button>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteEntity('Crop', crop.id); }} className="text-red-500 p-1"><Trash2 size={16}/></button>
                    </div>
                  </div>
                </div>
              ))}

              {selectedFarm && (
                <form onSubmit={handleCreateCrop} className="mt-6 p-4 bg-[#1B263B]/5 dark:bg-white/5 rounded-xl border border-dashed border-[#1B263B]/20">
                  <h4 className="text-sm font-bold mb-3 flex items-center gap-2"><Plus size={14}/> Add New Crop</h4>
                  <div className="space-y-2">
                    <input type="text" placeholder="Crop Name (e.g. Wheat)" value={newCrop.crop_name} onChange={e=>setNewCrop({...newCrop, crop_name:e.target.value})} className="w-full text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" required />
                    <div className="flex gap-2">
                      <input type="number" placeholder="Area" value={newCrop.planted_area} onChange={e=>setNewCrop({...newCrop, planted_area:e.target.value})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" />
                      <input type="number" placeholder="Yield Cap" value={newCrop.yield_capacity} onChange={e=>setNewCrop({...newCrop, yield_capacity:e.target.value})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" />
                    </div>
                    <div className="flex gap-2">
                      <input type="number" placeholder="Age" value={newCrop.age} onChange={e=>setNewCrop({...newCrop, age:e.target.value})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" />
                      <select value={newCrop.health_status} onChange={e=>setNewCrop({...newCrop, health_status:e.target.value})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none">
                        <option value="Healthy">Healthy</option>
                        <option value="Stress Detected">Stress Detected</option>
                        <option value="Diseased">Diseased</option>
                      </select>
                    </div>
                    <button type="submit" className="w-full bg-[#C47A2C] text-white rounded p-2 text-sm font-bold">Add Crop</button>
                  </div>
                </form>
              )}
            </div>
          </Card>

          {/* TREES LIST */}
          <Card className={`flex flex-col h-full overflow-hidden transition-opacity ${!selectedCrop ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="p-4 border-b border-[#1B263B]/10 dark:border-white/10 flex justify-between items-center bg-[#1B263B]/5 dark:bg-white/5">
              <h3 className="font-bold flex items-center gap-2"><Leaf size={18} className="text-[#C47A2C]"/> Trees in {selectedCrop?.crop_name || 'Crop'}</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
              {trees.map(tree => (
                <div key={tree.id} className="p-3 rounded-xl border border-[#1B263B]/10 dark:border-white/10 hover:border-[#C47A2C]/50 transition-all">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold">{tree.tree_name} ({tree.tree_code})</div>
                      <div className="text-xs opacity-60 mt-1">Lat: {tree.latitude} | Lng: {tree.longitude}</div>
                      <div className="text-xs opacity-60 mt-1">Yield Cap: {tree.yield_capacity} | Age: {tree.age} | Health: {tree.health_status}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setSelectedEntityForMedia({ type: 'Tree', id: tree.id }); fetchMedia('Tree', tree.id); }} className="text-blue-500 p-1"><ImageIcon size={16}/></button>
                      <button onClick={() => handleDeleteEntity('Tree', tree.id)} className="text-red-500 p-1"><Trash2 size={16}/></button>
                    </div>
                  </div>
                </div>
              ))}

              {selectedCrop && (
                <form onSubmit={handleCreateTree} className="mt-6 p-4 bg-[#1B263B]/5 dark:bg-white/5 rounded-xl border border-dashed border-[#1B263B]/20">
                  <h4 className="text-sm font-bold mb-3 flex items-center gap-2"><Plus size={14}/> Add New Tree</h4>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input type="text" placeholder="Name" value={newTree.tree_name} onChange={e=>setNewTree({...newTree, tree_name:e.target.value})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" required />
                      <input type="text" placeholder="Code" value={newTree.tree_code} onChange={e=>setNewTree({...newTree, tree_code:e.target.value})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" required />
                    </div>
                    <div className="flex gap-2">
                      <input type="number" placeholder="Lat" value={newTree.latitude} onChange={e=>setNewTree({...newTree, latitude:parseFloat(e.target.value)})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" step="any" />
                      <input type="number" placeholder="Lng" value={newTree.longitude} onChange={e=>setNewTree({...newTree, longitude:parseFloat(e.target.value)})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" step="any" />
                    </div>
                    <div className="h-32 rounded overflow-hidden relative z-0">
                      <MapContainer center={[newTree.latitude || newFarm.latitude || 30.0444, newTree.longitude || newFarm.longitude || 31.2357]} zoom={15} style={{ height: '100%', width: '100%' }}>
                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                        <LocationPicker lat={newTree.latitude} lng={newTree.longitude} setLocation={(lat, lng) => setNewTree({...newTree, latitude: lat, longitude: lng})} />
                      </MapContainer>
                    </div>
                    <div className="flex gap-2">
                      <input type="number" placeholder="Yield Cap" value={newTree.yield_capacity} onChange={e=>setNewTree({...newTree, yield_capacity:e.target.value})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none" />
                      <select value={newTree.health_status} onChange={e=>setNewTree({...newTree, health_status:e.target.value})} className="w-1/2 text-sm p-2 rounded bg-white dark:bg-[#162030] outline-none">
                        <option value="Healthy">Healthy</option>
                        <option value="Stress Detected">Stress Detected</option>
                        <option value="Diseased">Diseased</option>
                      </select>
                    </div>
                    <button type="submit" className="w-full bg-[#C47A2C] text-white rounded p-2 text-sm font-bold">Add Tree</button>
                  </div>
                </form>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* MEDIA OVERLAY */}
      <AnimatePresence>
        {selectedEntityForMedia && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="bg-white dark:bg-[#0A0F1E] w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl flex flex-col"
              style={{ maxHeight: '90vh' }}
            >
              <div className="flex justify-between items-center p-4 border-b border-[#1B263B]/10 dark:border-white/10">
                <h3 className="font-bold text-lg flex items-center gap-2"><ImageIcon className="text-[#C47A2C]"/> {selectedEntityForMedia.type} Media Gallery</h3>
                <button onClick={() => setSelectedEntityForMedia(null)} className="p-1 hover:bg-black/5 dark:hover:bg-white/5 rounded-full"><X size={20}/></button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                {mediaGallery.length === 0 ? (
                  <div className="text-center py-12 opacity-50 flex flex-col items-center">
                    <ImageIcon size={48} className="mb-4 opacity-20" />
                    <p>No media uploaded yet.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {mediaGallery.map((m, i) => (
                      <div key={i} className="aspect-square rounded-xl overflow-hidden bg-black/5 relative group">
                        <img src={getFullImageUrl(m.file_url || m.file_path)} alt="Asset Media" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-[#1B263B]/10 dark:border-white/10 bg-[#1B263B]/5 dark:bg-white/5">
                <input type="file" multiple ref={multipleFileInputRef} className="hidden" accept="image/*,video/*" onChange={handleMultiMediaUpload} />
                <button onClick={() => multipleFileInputRef.current?.click()} className="w-full flex items-center justify-center gap-2 bg-[#C47A2C] text-white py-3 rounded-xl font-bold hover:bg-[#A36322] transition-colors">
                  <Upload size={18} /> Upload Photos
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
